import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { FEATURES } from '@yuan-math/config';
import { bank, registry } from '@yuan-math/content';
import { MISCONCEPTIONS, type GradeResult } from '@yuan-math/exercises';
import {
  acceptQuest as applyAcceptQuest,
  applyAttempt,
  claimQuestReward as applyClaimQuestReward,
  entitlementsFor,
  practiceAllowance,
  applyDiagnostic,
  completeLesson,
  createCharacter as applyCreateCharacter,
  markJournalSeen as applyMarkJournalSeen,
  recordTrickNamed as applyRecordTrickNamed,
  recordBattleAnswer as applyRecordBattleAnswer,
  resetBattle as applyResetBattle,
  updateAvatar as applyUpdateAvatar,
  computeMastery,
  createProfile,
  dailyQuests,
  levelForXp,
  knownSet,
  masteredSet,
  recommendNext,
  registerRemediation,
  rollDaily,
  weeklyGoal,
  type AgeBand,
  type Achievement,
  type AvatarConfig,
  type BossAnswer,
  type DailyProgress,
  type DiagnosticSummary,
  type LearnerProfile,
  type Entitlements,
  type MasteryBreakdown,
  type PracticeAllowance,
  type Quest,
  type QuestStatus,
  type RpgClassId,
  type Subscription,
  type Recommendation,
  type WeeklyGoal,
} from '@yuan-math/learning';
import {
  buyItem as applyBuyItem,
  claimItem as applyClaimItem,
  equipItem as applyEquipItem,
  settleBossVictory as applySettleBossVictory,
  settleSiteVictory as applySettleSiteVictory,
  settleTrialVictory as applySettleTrialVictory,
  placeBuild as applyPlaceBuild,
  unequipItem as applyUnequipItem,
  type BattleState,
  type BossDef,
  type ItemDef,
  type ItemSlot,
  type Position,
  type TileGrid,
} from '@yuan-math/world';
import {
  clearProfile,
  deleteRemoteProfile,
  loadProfile,
  pushRemoteProfile,
  saveProfile,
  syncOnSignIn,
} from './storage.ts';
import { fetchSubscription } from './billing.ts';
import { useAuth } from './AuthContext.tsx';

/**
 * The single source of truth for learner state in the UI.
 *
 * All the rules live in `@yuan-math/learning` as pure functions; this context
 * only holds the current profile, persists it, and exposes the derived values
 * screens need. Keeping it this thin is what makes the business logic testable
 * without React.
 */

registerRemediation(MISCONCEPTIONS);

/** How long to wait after the last change before writing to the server. */
const SYNC_DEBOUNCE_MS = 4000;

export type SyncState = 'off' | 'idle' | 'syncing' | 'saved' | 'error';

export type AttemptOptions = {
  conceptId: string;
  exerciseId: string;
  difficulty: number;
  result: GradeResult;
  responseMs?: number;
  usedHint?: boolean;
  firstTry: boolean;
  isReview?: boolean;
};

export type AttemptFeedback = {
  /** XP for the answer itself, combo included. */
  xpGained: number;
  /** XP paid for any quest this answer finished. */
  bonusXp: number;
  justMastered: boolean;
  unlockedAchievements: Achievement[];
  masteryAfter: MasteryBreakdown;
  /** Consecutive correct answers after this one. */
  combo: number;
  completedQuests: Quest[];
  sweptTheDay: boolean;
  leveledUpTo?: number;
  /** The title of the level just reached, when one was. */
  levelTitle?: string;
};

export type LessonFeedback = {
  xpGained: number;
  bonusXp: number;
  unlockedAchievements: Achievement[];
  completedQuests: Quest[];
  sweptTheDay: boolean;
};

type LearnerContextValue = {
  profile: LearnerProfile | null;
  /** True until the stored profile has been read, to avoid a flash of signup. */
  ready: boolean;
  /**
   * True while a signed-in account's profile is still being fetched.
   *
   * Distinct from `ready`, which only covers local storage. On a new device
   * there is nothing local to read, and the profile arrives over the network a
   * moment later — so a guard that looked only at `ready` would decide the
   * learner does not exist and redirect before their progress landed.
   */
  resolving: boolean;
  signedIn: boolean;
  createLearner(input: { displayName: string; ageBand: AgeBand }): void;
  recordAttempt(options: AttemptOptions): AttemptFeedback;
  finishLesson(input: { lessonId: string; conceptId: string }): LessonFeedback;
  saveDiagnostic(summary: DiagnosticSummary): void;
  setGoal(conceptId: string | undefined): void;
  signOut(): Promise<void>;
  /** Erases the learner from this device and from the server. */
  deleteLearner(): Promise<void>;
  /** Mastery for one concept, at the current moment. */
  masteryOf(conceptId: string): MasteryBreakdown;
  masteredConceptIds: Set<string>;
  /**
   * What the student has demonstrated *or* the placement test credited them
   * with — the set to ask "can they do this yet?" with.
   *
   * Kept apart from `masteredConceptIds`, which is the set the platform makes
   * claims from. A placement estimate is good enough to decide what to offer
   * someone and not good enough to put a "mastered" badge on their map.
   */
  knownConceptIds: Set<string>;
  recommendations: Recommendation[];
  /** Whether progress is being kept on the server, and how it is going. */
  syncState: SyncState;
  /** The account's subscription, or undefined for free. */
  subscription: Subscription | undefined;
  /** What the current tier grants. Free when there is no subscription. */
  entitlements: Entitlements;
  /** How much practice is left today. Unlimited on a paid tier. */
  allowance: PracticeAllowance;
  /** Today's three quests, with progress. Empty without a learner. */
  quests: QuestStatus[];
  /** Progress against the student's own weekly target. */
  weekly: WeeklyGoal;
  /** Today's counters — the live combo, mostly. */
  today: DailyProgress;
  /** Sets how many days a week the student is aiming for. */
  setWeeklyGoalDays(days: number): void;
  /** Creates the RPG character, or re-edits it. A costume, never a curriculum. */
  createCharacter(input: { classId: RpgClassId; avatar: AvatarConfig }): void;
  /** Redresses the character without touching its class. */
  updateAvatar(avatar: AvatarConfig): void;
  /**
   * Takes a story quest on. A no-op once accepted, so `acceptedAt` never
   * moves — which matters because a `practice` objective counts from it.
   */
  acceptQuest(questId: string): void;
  /**
   * Pays a finished quest's reward, once. Idempotent in the reducer, so a
   * double-tap, a retry or two devices merging cannot pay twice.
   */
  claimQuestReward(input: { questId: string; xp: number; gold: number }): void;
  /**
   * Appends one graded boss answer to the fight in progress.
   *
   * The battle itself is never stored — `replayBattle` re-derives it from this
   * list — and malformed input never gets here, because the boss page checks
   * the status first and the battle reducer refuses it anyway.
   */
  recordBattleAnswer(input: { battleId: string; attemptIndex: number; answer: BossAnswer }): void;
  /** Fights it again, from the top, with fresh problems. Costs nothing. */
  resetBattle(battleId: string): void;
  /**
   * Records that the student correctly named a mistake, in an encounter's
   * closing beat.
   *
   * **This is not evidence about mathematics and nothing may read it as such.**
   * Recognising an error is not the same as having stopped making it, so the
   * reducer writes one id into the RPG slice and goes nowhere near `concepts`,
   * `attempts`, `stats` or `xp`. The only thing it drives is the bestiary's
   * "named" mark.
   */
  recordTrickNamed(misconceptionId: string): void;
  /**
   * Marks Adventure Journal entries as read.
   *
   * The journal's only stored fact — every entry itself is derived from the
   * attempt log. Idempotent in the reducer, and a no-op when there is nothing
   * new, so the page can call it on every mount without writing anything.
   */
  markJournalSeen(entryIds: readonly string[]): void;
  /**
   * Records a won fight and pays for it, once.
   *
   * The rule lives in `@yuan-math/world` — `recordBossClear` plus an
   * idempotent claim under `boss:<id>` — so a re-fought boss is recorded again
   * and paid nothing. This context only dispatches it, like every other
   * transition here.
   */
  settleBossVictory(def: BossDef, state: BattleState): void;
  /**
   * Pays for a cleared warded site, once ever.
   *
   * The same shape one scale down, and the same idempotent claim — under
   * `site:<conceptId>` this time. A site re-fought for the pleasure of it pays
   * nothing (docs/rpg.md §47), and unlike a boss there is nothing else to
   * record: whether the student can do the concept is a question the attempt
   * log already answers better than a flag would.
   */
  settleSiteVictory(conceptId: string, outcome: string): void;
  /**
   * Pays for a cleared North Reach trial, once, through the same idempotent
   * claim. Like a site, there is nothing else to record — every answer in the
   * fight already went through `recordAttempt` on the ordinary path.
   */
  settleTrialVictory(trialId: string, outcome: string): void;
  /**
   * Puts a bridge, an island or a house on the overworld.
   *
   * Every rule — the ten questions, and what may stand on what ground — lives
   * in `@yuan-math/world`; this only dispatches, like every transition here. A
   * refused build hands the profile straight back, so a misplaced tap costs
   * nothing and writes nothing.
   */
  placeBuild(world: TileGrid, kind: string, position: Position): void;
  /**
   * Claims an earned cosmetic, or buys a shop one.
   *
   * One entry point rather than two, because the wardrobe tile is one button
   * and which reducer runs is a property of the item. Every rule — condition
   * met, gold sufficient, not already owned — lives in `@yuan-math/world` and
   * `spendGold`; this context only dispatches, like every transition here.
   */
  acquireItem(def: ItemDef): void;
  /** Wears an owned cosmetic, replacing whatever was in its slot. */
  equipItem(def: ItemDef): void;
  /** Takes whatever is in a slot off. */
  unequipItem(slot: ItemSlot): void;
  /** Forces an immediate write. Used before signing out. */
  flushSync(): Promise<void>;
};

const LearnerContext = createContext<LearnerContextValue | null>(null);

/**
 * The learner an account describes, for a device that has never seen one.
 *
 * Registration asks for a name and an age band, so an account that has
 * practised nowhere yet still knows enough to be a learner. Without this, the
 * first sign-in on a new device finds no profile locally and none on the
 * server, and the app concludes there is no student — showing a signed-in
 * person the guest header and the "let's find your starting point" form they
 * filled in when they registered.
 *
 * The id is derived from the account rather than the clock so that two devices
 * racing to adopt the same account converge on one row instead of two.
 */
function profileForAccount(
  accountId: string,
  details: { displayName?: string; ageBand?: AgeBand },
  now: number,
): LearnerProfile | null {
  if (!details.displayName || !details.ageBand) return null;
  return createProfile({
    id: `learner-${accountId}`,
    displayName: details.displayName,
    ageBand: details.ageBand,
    now,
  });
}

export function LearnerProvider({ children }: { children: ReactNode }): JSX.Element {
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [ready, setReady] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>('off');
  const [subscription, setSubscription] = useState<Subscription | undefined>(undefined);
  // The account whose first reconciliation has completed, so a returning
  // learner is never judged absent while their profile is still in flight.
  const [syncedAccountId, setSyncedAccountId] = useState<string | undefined>(undefined);

  const { account, signedIn, ready: authReady, signOut: authSignOut } = useAuth();
  const accountId = signedIn ? account?.id : undefined;

  // What the account says about its learner, in a ref so reconciliation can
  // read the newest values without re-running every time the object is
  // replaced by an equal one.
  const accountDetails = useRef<{ displayName?: string; ageBand?: AgeBand }>({});
  accountDetails.current = { displayName: account?.displayName, ageBand: account?.ageBand };

  // Held in a ref so the debounced writer always sees the newest profile
  // without re-arming the timer being a dependency problem.
  const latest = useRef<LearnerProfile | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const stored = loadProfile();
    setProfile(stored);
    latest.current = stored;
    setReady(true);
  }, []);

  // Local write is synchronous and unconditional: it must never depend on a
  // network, an account, or anything that can fail.
  useEffect(() => {
    latest.current = profile;
    if (profile) saveProfile(profile);
  }, [profile]);

  /**
   * On sign-in, reconcile with the server.
   *
   * This is where a guest's work meets an account's history. `syncOnSignIn`
   * merges rather than choosing, so neither side is lost.
   */
  // Subscription state is read, never written, from here: `subscriptions` has
  // no insert or update policy, so only the Stripe webhook can grant a tier.
  useEffect(() => {
    if (!accountId) {
      setSubscription(undefined);
      return;
    }
    let cancelled = false;
    void fetchSubscription(accountId).then((found) => {
      if (!cancelled) setSubscription(found);
    });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (!accountId) {
      setSyncState('off');
      setSyncedAccountId(undefined);
      return;
    }
    let cancelled = false;

    const reconcile = async (): Promise<void> => {
      setSyncState('syncing');
      try {
        const merged = await syncOnSignIn(accountId, latest.current);
        if (cancelled) return;
        // Nothing here and nothing there: a first sign-in on this device for an
        // account that has never practised. Adopt the learner it registered as
        // rather than treating the student as absent. The debounced writer
        // sends it on, so the next device finds it waiting.
        const resolved = merged ?? profileForAccount(accountId, accountDetails.current, Date.now());
        if (resolved) {
          setProfile(resolved);
          latest.current = resolved;
        }
        setSyncState('saved');
      } catch {
        if (!cancelled) setSyncState('error');
      } finally {
        // Marked done however it ended. A failed fetch must not leave the
        // guard waiting forever — falling back to "no learner" is recoverable,
        // an indefinite spinner is not.
        if (!cancelled) setSyncedAccountId(accountId);
      }
    };
    void reconcile();

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  /**
   * Push changes on a debounce.
   *
   * A practice session generates a write per answered question; sending each
   * one would be a request every few seconds for no benefit. Waiting for a
   * pause costs nothing, because the local copy is already saved.
   */
  useEffect(() => {
    if (!accountId || !profile) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSyncState('syncing');
      void pushRemoteProfile(accountId, profile).then((ok) => setSyncState(ok ? 'saved' : 'error'));
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [accountId, profile]);

  /**
   * Write immediately rather than on the debounce.
   *
   * Called before signing out, and on page hide — closing a tab mid-session
   * must not cost the student the last few minutes of work.
   */
  const flushSync = useCallback(async (): Promise<void> => {
    if (!accountId || !latest.current) return;
    if (timer.current) clearTimeout(timer.current);
    setSyncState('syncing');
    const ok = await pushRemoteProfile(accountId, latest.current);
    setSyncState(ok ? 'saved' : 'error');
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') void flushSync();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [accountId, flushSync]);

  const createLearner = useCallback((input: { displayName: string; ageBand: AgeBand }) => {
    setProfile(
      createProfile({
        id: `learner-${Date.now().toString(36)}`,
        displayName: input.displayName.trim() || 'Learner',
        ageBand: input.ageBand,
        now: Date.now(),
      }),
    );
  }, []);

  const recordAttempt = useCallback((options: AttemptOptions): AttemptFeedback => {
    let feedback: AttemptFeedback = {
      xpGained: 0,
      bonusXp: 0,
      justMastered: false,
      unlockedAchievements: [],
      masteryAfter: computeMastery(undefined, Date.now()),
      combo: 0,
      completedQuests: [],
      sweptTheDay: false,
    };

    setProfile((current) => {
      if (!current) return current;
      const outcome = applyAttempt(current, { ...options, now: Date.now() });
      feedback = {
        xpGained: outcome.xpGained,
        bonusXp: outcome.bonusXp,
        justMastered: outcome.justMastered,
        unlockedAchievements: outcome.unlockedAchievements,
        masteryAfter: outcome.masteryAfter,
        combo: outcome.combo,
        completedQuests: outcome.completedQuests,
        sweptTheDay: outcome.sweptTheDay,
        ...(outcome.leveledUpTo !== undefined
          ? { leveledUpTo: outcome.leveledUpTo, levelTitle: levelForXp(outcome.profile.xp).title }
          : {}),
      };
      return outcome.profile;
    });

    return feedback;
  }, []);

  const finishLesson = useCallback((input: { lessonId: string; conceptId: string }): LessonFeedback => {
    let feedback: LessonFeedback = {
      xpGained: 0,
      bonusXp: 0,
      unlockedAchievements: [],
      completedQuests: [],
      sweptTheDay: false,
    };

    setProfile((current) => {
      if (!current) return current;
      const outcome = completeLesson(current, { ...input, now: Date.now() });
      feedback = {
        xpGained: outcome.xpGained,
        bonusXp: outcome.bonusXp,
        unlockedAchievements: outcome.unlockedAchievements,
        completedQuests: outcome.completedQuests,
        sweptTheDay: outcome.sweptTheDay,
      };
      return outcome.profile;
    });

    return feedback;
  }, []);

  const setWeeklyGoalDays = useCallback((days: number) => {
    setProfile((current) =>
      current ? { ...current, weeklyGoalDays: Math.max(1, Math.min(7, Math.round(days))) } : current,
    );
  }, []);

  const saveDiagnostic = useCallback((summary: DiagnosticSummary) => {
    setProfile((current) =>
      current
        ? applyDiagnostic(current, {
            assumedMastered: summary.assumedMastered,
            demonstrated: summary.demonstrated,
            summary,
            now: Date.now(),
          })
        : current,
    );
  }, []);

  const setGoal = useCallback((conceptId: string | undefined) => {
    setProfile((current) => (current ? { ...current, goalConceptId: conceptId } : current));
  }, []);

  // Both RPG transitions dispatch straight into `@yuan-math/learning` reducers;
  // no rule lives here, per the contract at the top of this file.
  const createCharacter = useCallback((input: { classId: RpgClassId; avatar: AvatarConfig }) => {
    setProfile((current) => (current ? applyCreateCharacter(current, { ...input, now: Date.now() }) : current));
  }, []);

  const updateAvatar = useCallback((avatar: AvatarConfig) => {
    setProfile((current) => (current ? applyUpdateAvatar(current, { avatar, now: Date.now() }) : current));
  }, []);

  const acceptQuest = useCallback((questId: string) => {
    setProfile((current) => (current ? applyAcceptQuest(current, { questId, now: Date.now() }) : current));
  }, []);

  const claimQuestReward = useCallback((input: { questId: string; xp: number; gold: number }) => {
    setProfile((current) => (current ? applyClaimQuestReward(current, input) : current));
  }, []);

  const recordBattleAnswer = useCallback((input: { battleId: string; attemptIndex: number; answer: BossAnswer }) => {
    setProfile((current) => (current ? applyRecordBattleAnswer(current, input) : current));
  }, []);

  const recordTrickNamed = useCallback((misconceptionId: string) => {
    setProfile((current) => (current ? applyRecordTrickNamed(current, { misconceptionId }) : current));
  }, []);

  const resetBattle = useCallback((battleId: string) => {
    setProfile((current) => (current ? applyResetBattle(current, { battleId }) : current));
  }, []);

  const markJournalSeen = useCallback((entryIds: readonly string[]) => {
    setProfile((current) => (current ? applyMarkJournalSeen(current, { entryIds }) : current));
  }, []);

  const settleBossVictory = useCallback((def: BossDef, state: BattleState) => {
    setProfile((current) => (current ? applySettleBossVictory(current, def, state) : current));
  }, []);

  const settleSiteVictory = useCallback((conceptId: string, outcome: string) => {
    setProfile((current) => (current ? applySettleSiteVictory(current, conceptId, outcome) : current));
  }, []);

  const settleTrialVictory = useCallback((trialId: string, outcome: string) => {
    setProfile((current) => (current ? applySettleTrialVictory(current, trialId, outcome) : current));
  }, []);

  const placeBuild = useCallback((world: TileGrid, kind: string, position: Position) => {
    setProfile((current) =>
      current ? applyPlaceBuild(world, current, kind, position, Date.now()) : current,
    );
  }, []);

  const acquireItem = useCallback((def: ItemDef) => {
    setProfile((current) => {
      if (!current) return current;
      // The branch is the item's, not the caller's: an earned piece is claimed
      // and a shop piece is bought, and neither reducer will do the other's job.
      return def.source.kind === 'shop'
        ? applyBuyItem(current, def)
        : applyClaimItem(current, def, registry.graph, Date.now());
    });
  }, []);

  const equipItem = useCallback((def: ItemDef) => {
    setProfile((current) => (current ? applyEquipItem(current, def) : current));
  }, []);

  const unequipItem = useCallback((slot: ItemSlot) => {
    setProfile((current) => (current ? applyUnequipItem(current, slot) : current));
  }, []);

  /**
   * Ends the session and clears this device.
   *
   * Both halves, deliberately in one function. There used to be two sign-outs —
   * this one dropped the local profile while the Supabase session survived — so
   * a refresh restored the account, pulled the profile back down, and the user
   * was signed in again having explicitly asked not to be. On a shared computer
   * that is a serious failure, not an annoyance.
   *
   * The order matters: outstanding work is pushed first, while the session is
   * still valid to push it with.
   */
  /**
   * Erases this learner everywhere (§29).
   *
   * Deliberately not built on `signOut`: that flushes pending work to the
   * server first, which for a deletion would upload the very profile the person
   * asked to remove and then leave it there after the local copy was cleared.
   * The server row goes first, then the device, then the session.
   */
  const deleteLearner = useCallback(async (): Promise<void> => {
    const id = latest.current?.id;
    if (accountId && id) await deleteRemoteProfile(accountId, id).catch(() => undefined);
    clearProfile();
    setProfile(null);
    latest.current = null;
    await authSignOut().catch(() => undefined);
  }, [accountId, authSignOut]);

  const signOut = useCallback(async (): Promise<void> => {
    await flushSync().catch(() => undefined);
    // Ends the Supabase session and clears its stored tokens.
    await authSignOut().catch(() => undefined);
    clearProfile();
    setProfile(null);
    latest.current = null;
  }, [flushSync, authSignOut]);

  // Recomputed per render of the provider rather than memoised on `Date.now()`,
  // which would be a lie: mastery genuinely decays with time.
  const now = Date.now();

  const masteredConceptIds = useMemo(
    () => (profile ? masteredSet(profile.concepts, now) : new Set<string>()),
    [profile, now],
  );

  const knownConceptIds = useMemo(
    () => (profile ? knownSet(profile.concepts, now) : new Set<string>()),
    [profile, now],
  );

  const recommendations = useMemo(
    () => (profile ? recommendNext({ registry, profile, now }, 4) : []),
    [profile, now],
  );

  /*
   * Whether the first reconciliation for the current account has finished.
   *
   * Tracked explicitly rather than inferred from `syncState`: that value is set
   * by a different effect and passes through several states, so there is a
   * window where it reads as settled before the fetch has started. A guard
   * built on it redirects a returning learner to "create a learner" a moment
   * before their months of progress arrive.
   *
   * It also covers the step before that one. Restoring a stored session is
   * asynchronous, so for the first few frames of a page load there is no
   * account *yet* — and a guard reading only this account's reconciliation
   * would see nothing to wait for and redirect on the way past, which is how a
   * signed-in student opening `/home` directly landed on the sign-up form.
   */
  const resolving = !authReady || (Boolean(accountId) && syncedAccountId !== accountId);

  /**
   * What this learner is entitled to.
   *
   * With `FEATURES.payments` off there is nothing to sell, so the free tier's
   * daily practice cap is lifted here rather than in `packages/learning`: the
   * tier table describes what a tier *grants*, and whether the platform takes
   * money at all is an application-level fact. Keeping the two apart is what
   * lets `entitlements.test.ts` go on asserting that free means 30 a day.
   *
   * Only the cap moves. Tier, `advancedCourses` and the rest are untouched, so
   * an existing subscriber keeps exactly what they paid for.
   */
  const entitlements = useMemo(() => {
    const granted = entitlementsFor(subscription, now);
    if (FEATURES.payments) return granted;
    return { ...granted, dailyProblemLimit: undefined };
  }, [subscription, now]);

  const allowance = useMemo(
    () =>
      profile
        ? practiceAllowance(profile, entitlements, now)
        : { usedToday: 0, limit: entitlements.dailyProblemLimit, remaining: entitlements.dailyProblemLimit, exhausted: false },
    [profile, entitlements, now],
  );

  // Quests and the weekly goal are derived, never stored twice: the profile is
  // the only record, so a stale copy here could not disagree with it.
  const quests = useMemo(() => (profile ? dailyQuests(profile, now) : []), [profile, now]);
  const weekly = useMemo(
    () => (profile ? weeklyGoal(profile, now) : { target: 5, days: [], daysActive: 0, fraction: 0, complete: false }),
    [profile, now],
  );
  const today = useMemo(
    () => (profile ? rollDaily(profile, now) : rollDaily(createProfile({ id: 'guest', displayName: '', now }), now)),
    [profile, now],
  );

  const masteryOf = useCallback(
    (conceptId: string): MasteryBreakdown => computeMastery(profile?.concepts[conceptId], Date.now()),
    [profile],
  );

  const value = useMemo<LearnerContextValue>(
    () => ({
      profile,
      ready,
      signedIn: profile !== null,
      createLearner,
      recordAttempt,
      finishLesson,
      saveDiagnostic,
      setGoal,
      signOut,
      deleteLearner,
      masteryOf,
      masteredConceptIds,
      knownConceptIds,
      recommendations,
      syncState,
      flushSync,
      resolving,
      subscription,
      entitlements,
      allowance,
      quests,
      weekly,
      today,
      setWeeklyGoalDays,
      createCharacter,
      updateAvatar,
      acceptQuest,
      claimQuestReward,
      recordBattleAnswer,
      recordTrickNamed,
      resetBattle,
      settleBossVictory,
      settleSiteVictory,
      settleTrialVictory,
      placeBuild,
      markJournalSeen,
      acquireItem,
      equipItem,
      unequipItem,
    }),
    [profile, ready, createLearner, recordAttempt, finishLesson, saveDiagnostic, setGoal, signOut, deleteLearner, masteryOf, masteredConceptIds, knownConceptIds, recommendations, syncState, flushSync, resolving, subscription, entitlements, allowance, quests, weekly, today, setWeeklyGoalDays, createCharacter, updateAvatar, acceptQuest, claimQuestReward, recordBattleAnswer, recordTrickNamed, resetBattle, settleBossVictory, settleSiteVictory, markJournalSeen, acquireItem, equipItem, unequipItem],
  );

  return <LearnerContext.Provider value={value}>{children}</LearnerContext.Provider>;
}

export function useLearner(): LearnerContextValue {
  const context = useContext(LearnerContext);
  if (!context) throw new Error('useLearner must be used inside a LearnerProvider');
  return context;
}

/** The curriculum and exercise singletons, for screens that need them. */
export { registry, bank };
